import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Send,
  CheckCircle,
  Error as ErrorIcon,
  Facebook,
  Instagram,
  LinkedIn,
  X,
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  service: string;
}

const Contact: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      service: '',
    },
  });

  const services = [
    'ITR Filing',
    'Tax Planning',
    'Business Tax',
    'GST Services',
    'Compliance',
    'Business Consulting',
    'Other',
  ];

  const serviceFromQuery = searchParams.get('service')?.trim() || '';
  const isSubmitted = searchParams.get('submitted') === 'true';
  const availableServices = serviceFromQuery && !services.includes(serviceFromQuery)
    ? [serviceFromQuery, ...services]
    : services;

  React.useEffect(() => {
    if (serviceFromQuery) {
      setValue('service', serviceFromQuery, { shouldValidate: true });
    }
  }, [serviceFromQuery, setValue]);

  React.useEffect(() => {
    if (isSubmitted) {
      setSubmitSuccess(true);
      setOpenSnackbar(true);
    }
  }, [isSubmitted]);

  const submitViaPostFallback = (data: ContactFormData) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://formsubmit.co/fingardpartners@gmail.com';
    form.style.display = 'none';

    const fields: Record<string, string> = {
      notification:
        'We have received a new message from the website contact form. Please review the details below.',
      full_name: data.name,
      email: data.email,
      phone: data.phone,
      service: data.service,
      subject: data.subject,
      message: data.message,
      _subject: 'New Client Inquiry | Fingard Website',
      _template: 'table',
      _captcha: 'false',
      _replyto: data.email,
      _next: `${window.location.origin}/contact?submitted=true`,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError('');

      const response = await fetch('https://formsubmit.co/ajax/fingardpartners@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          notification: 'We have received a new message from the website contact form. Please review the details below.',
          full_name: data.name,
          email: data.email,
          phone: data.phone,
          service: data.service,
          subject: data.subject,
          message: data.message,
          _subject: `New Client Inquiry | Fingard Website`,
          _template: 'table',
          _captcha: 'false',
          _replyto: data.email,
        }),
      });

      const result = await response.json();
      const isSuccess =
        response.ok &&
        (result?.success === true ||
          result?.success === 'true' ||
          (typeof result?.message === 'string' && result.message.toLowerCase().includes('submitted')));

      if (!isSuccess) {
        throw new Error(
          result?.message || 'Unable to submit right now. Please try again in a moment.'
        );
      }

      // Also automatically push lead to Fingard Sales CRM
      try {
        const apiBase = process.env.REACT_APP_API_URL || 
          (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? 'http://localhost:5001'
            : 'https://fingard-backend.onrender.com');

        fetch(`${apiBase}/api/register-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientName: data.name,
            clientEmail: data.email,
            clientPhone: data.phone,
            clientCity: 'India',
            serviceCategory: 'contact',
            selectedService: data.service ? `Consultation: ${data.service}` : (data.subject || 'General Consultation'),
            pricingAmount: 0,
            diagnosticAnswers: { subject: data.subject, message: data.message }
          })
        }).catch((e) => console.warn('Sales CRM sync note:', e));
      } catch (crmSyncErr) {
        // non-blocking
      }

      setSubmitSuccess(true);
      setOpenSnackbar(true);
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      // Fallback for environments where AJAX submit is blocked.
      submitViaPostFallback(data);
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const contactInfo = [
    {
      icon: <LocationOn color="primary" sx={{ fontSize: 32 }} />,
      title: 'Our Location',
      description: 'Plot No: 3-B, Sangram Colony Gate No. 2, Near Mahavir School, Mahavir Marg, C-Scheme, Jaipur-302001, Rajasthan, India',
      action: {
        text: 'View on Map',
        href: 'https://maps.app.goo.gl/example',
      },
    },
    {
      icon: <Phone color="primary" sx={{ fontSize: 32 }} />,
      title: 'Phone Number',
      description: '+91 91160 33575',
      action: {
        text: 'Call Now',
        href: 'tel:+91 91160 33575',
      },
    },
    {
      icon: <Email color="primary" sx={{ fontSize: 32 }} />,
      title: 'Email Address',
      description: 'support@fingard.co',
      action: {
        text: 'Send Email',
        href: 'mailto:fingardpartners@gmail.com',
      },
    },
    {
      icon: <Schedule color="primary" sx={{ fontSize: 32 }} />,
      title: 'Working Hours',
      description: 'Monday - Saturday: 9:00 AM - 7:00 PM\nSunday: Closed',
    },
  ];

  return (
    <Box>
      <SEOHead
        title="Contact Fingard Partners | Book a Free Tax Consultation"
        description="Get in touch with Fingard Partners for expert tax filing, GST registration, startup incorporation, and compliance services. Book a free consultation today."
        keywords="Contact Fingard Partners, Free Tax Consultation, CA firm contact, Tax advisor India"
        canonicalUrl="/contact"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="h6"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              opacity: 0.9,
              fontWeight: 400,
            }}
          >
            Have questions or need assistance? Get in touch with our team of experts.
          </Typography>
          
        </Container>
      </Box>

      {/* Contact Form & Info */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  Send us a Message
                </Typography>
                <Typography color="text.secondary" paragraph>
                  Fill out the form below and our team will get back to you within 24 hours.
                </Typography>

                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Full Name"
                            variant="outlined"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            variant="outlined"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="phone"
                        control={control}
                        rules={{
                          required: 'Phone number is required',
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: 'Please enter a valid 10-digit phone number',
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone Number"
                            type="tel"
                            variant="outlined"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="service"
                        control={control}
                        rules={{ required: 'Please select a service' }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.service}>
                            <InputLabel id="service-select-label">Service Interested In</InputLabel>
                            <Select
                              {...field}
                              labelId="service-select-label"
                              label="Service Interested In"
                              variant="outlined"
                            >
                              <MenuItem value="">
                                <em>Select a service</em>
                              </MenuItem>
                              {availableServices.map((service) => (
                                <MenuItem key={service} value={service}>
                                  {service}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.service && (
                              <FormHelperText>{errors.service.message}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Subject"
                            variant="outlined"
                            error={!!errors.subject}
                            helperText={errors.subject?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="message"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Your Message"
                            multiline
                            rows={5}
                            variant="outlined"
                            error={!!errors.message}
                            helperText={errors.message?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<Send />}
                      disabled={isSubmitting}
                      sx={{ minWidth: 180 }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: 24 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  Get in Touch
                </Typography>
                <Typography color="text.secondary" paragraph>
                  We'd love to hear from you. Here's how you can reach us.
                </Typography>

                <Box sx={{ mt: 4, '& > :not(style)': { mb: 3 } }}>
                  {contactInfo.map((info, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ mr: 2 }}>{info.icon}</Box>
                        <Box>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {info.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line' }}
                          >
                            {info.description}
                          </Typography>
                          {info.action && (
                            <Button
                              component="a"
                              href={info.action.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              size="small"
                              sx={{ mt: 1, pl: 0 }}
                            >
                              {info.action.text}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>

                {/* Social Media Links */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Follow Us
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[
                      { name: 'Facebook', icon: <Facebook fontSize="small" />, url: 'https://facebook.com/fingard' },
                      { name: 'X', icon: <X fontSize="small" />, url: 'https://x.com/fingard' },
                      { name: 'LinkedIn', icon: <LinkedIn fontSize="small" />, url: 'https://linkedin.com/company/fingard' },
                      { name: 'Instagram', icon: <Instagram fontSize="small" />, url: 'https://instagram.com/fingard' },
                    ].map((social) => (
                      <Button
                        key={social.name}
                        component="a"
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="large"
                        sx={{
                          minWidth: 48,
                          height: 48,
                          borderRadius: '50%',
                          p: 0,
                          borderColor: 'divider',
                          color: 'text.secondary',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                          },
                        }}
                        aria-label={social.name}
                      >
                        {social.icon}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Map Section */}
      <Box sx={{ height: 400, width: '100%' }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3558.123456789012!2d75.7872703150439!3d26.91242398315789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDU0JzQ0LjciTiA3NcKwNDcnMTQuMiJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          title="Fingard Location"
        ></iframe>
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={submitSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
          icon={
            submitSuccess ? (
              <CheckCircle fontSize="inherit" />
            ) : (
              <ErrorIcon fontSize="inherit" />
            )
          }
        >
          {submitSuccess
            ? 'Your message has been sent successfully! We will get back to you soon.'
            : submitError || 'An error occurred. Please try again.'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
